import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { normaliseSearch } from "@/lib/searchUtils";

export type NoteRow = Database["public"]["Tables"]["notes"]["Row"];

export interface NoteWithSeller extends Omit<NoteRow, "file_url"> {
  file_url?: string | null;
  has_file?: boolean;
  seller_is_seed?: boolean;
  profiles: {
    display_name: string | null;
    avatar_url: string | null;
    is_plus: boolean;
  } | null;
}

/** Map browsable_notes flat profile columns into the nested shape components expect */
function normalizeBrowsable(data: any[]): NoteWithSeller[] {
  return (data || []).map((d) => ({
    ...d,
    seller_is_seed: d.seller_is_seed ?? false,
    profiles: {
      display_name: d.seller_display_name ?? null,
      avatar_url: d.seller_avatar_url ?? null,
      is_plus: d.seller_is_plus ?? false,
    },
  }));
}

export type SortOption = "relevance" | "downloads" | "rating" | "reviews" | "price_low" | "price_high" | "newest";

export function useNotes(filters?: {
  search?: string;
  subject?: string;
  educationLevel?: string;
  examBoard?: string;
  sort?: SortOption;
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
}) {
  return useQuery({
    queryKey: ["notes", filters],
    queryFn: async () => {
      let query = supabase
        .from("browsable_notes")
        .select("*")
        .eq("status", "approved");

      if (filters?.subject && filters.subject !== "All") {
        query = query.ilike("subject", filters.subject);
      }
      if (filters?.educationLevel && filters.educationLevel !== "All") {
        query = query.ilike("education_level", `%${filters.educationLevel}%`);
      }
      if (filters?.examBoard && filters.examBoard !== "All") {
        query = query.ilike("exam_board", `%${filters.examBoard}%`);
      }
      if (filters?.minPrice !== undefined) {
        query = query.gte("price", filters.minPrice);
      }
      if (filters?.maxPrice !== undefined) {
        query = query.lte("price", filters.maxPrice);
      }
      if (filters?.minRating !== undefined) {
        query = query.gte("rating", filters.minRating);
      }

      const { data, error } = await query;
      if (error) throw error;

      let notes = normalizeBrowsable(data || []);

      if (filters?.search) {
        const normQ = normaliseSearch(filters.search);
        const terms = normQ.split(" ").filter(Boolean);
        notes = notes.filter((note) => {
          const haystack = [
            note.title,
            note.subject,
            note.course_code,
            note.education_level,
            note.exam_board || "",
            note.description || "",
            ...(note.tags || []),
          ]
            .join(" ")
            .toLowerCase();
          return terms.every((t) => haystack.includes(t));
        });
      }

      const sort = filters?.sort || "relevance";
      switch (sort) {
        case "downloads":
          notes.sort((a, b) => (b.download_count || 0) - (a.download_count || 0));
          break;
        case "rating":
          notes.sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
          break;
        case "reviews":
          notes.sort((a, b) => (b.review_count || 0) - (a.review_count || 0));
          break;
        case "price_low":
          notes.sort((a, b) => Number(a.price) - Number(b.price));
          break;
        case "price_high":
          notes.sort((a, b) => Number(b.price) - Number(a.price));
          break;
        case "newest":
          notes.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          break;
        default:
          notes.sort((a, b) => computeScore(b) - computeScore(a));
      }

      // Always push real-user notes above seeded/demo notes (stable sort)
      notes.sort((a, b) => Number(a.seller_is_seed ?? false) - Number(b.seller_is_seed ?? false));

      return notes;
    },
  });
}

function computeScore(note: NoteWithSeller): number {
  const rating = Number(note.rating || 0);
  const downloads = note.download_count || 0;
  const reviews = note.review_count || 0;
  const ageInDays = (Date.now() - new Date(note.created_at).getTime()) / 86400000;
  const isPlus = note.profiles?.is_plus ?? false;

  return (
    rating * 10 +
    Math.min(Math.log(Math.max(downloads, 1) + 1) * 5, 30) +
    Math.min(Math.log(Math.max(reviews, 1) + 1) * 3, 10) +
    Math.max(10 - ageInDays / 3, 0) +
    (isPlus ? 8 : 0)
  );
}

export function useNote(id: string | undefined) {
  return useQuery({
    queryKey: ["note", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("browsable_notes")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return normalizeBrowsable([data])[0];
    },
    enabled: !!id,
  });
}

export function useTrendingNotes(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["trending-notes"],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("browsable_notes")
        .select("*")
        .eq("status", "approved")
        .eq("seller_is_seed", false)
        .order("download_count", { ascending: false })
        .limit(3);
      if (error) throw error;
      return normalizeBrowsable(data || []);
    },
  });
}
